import CampoTexto from '../../components/Formulario/CampoTexto';
import Grid, { GridItem } from '../../components/layout/Grid';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import AlertBox from '../../components/layout/AlertBox';
import { TicketPrioridade } from '@tera/shared/src/dto/TicketDto';
import CampoData from '../../components/Formulario/CampoData';
import CampoMemo from '../../components/Formulario/CampoMemo';
import { addHours, format } from 'date-fns';
import HBox from '@/components/layout/HBox';
import { CampoSeletorCliente } from '@/components/modelos/CampoSeletorCliente';
import Modal from '@/components/layout/Modal';
import { FormCliente } from '../cliente/FormCliente';
import { useState } from 'react';
import { queryClient } from '@/queryClient';
import { toast } from 'react-toastify';

export const FormTicket = ({
    value,
    onChange,
    fieldErrors,
}: {
    value: any;
    onChange: any;
    fieldErrors: any;
}) => {
    const isNew = !value?.id || value?.id < 0;
    const isEditing = !isNew;

    // Fetch users for selector
    const usuariosQuery = useQuery(['usuarios'], async () => {
        const { data } = await axios.get('/api/usuario', {
            params: { take: 100 },
            withCredentials: true,
        });
        return data.result;
    });

    // Fetch groups for selector
    const gruposQuery = useQuery(['grupos'], async () => {
        const { data } = await axios.get('/api/usuario_grupo', {
            params: { take: 100 },
            withCredentials: true,
        });
        return data.result;
    });

    // Priority options
    const prioridadeOptions = [
        { id: 'baixa', label: 'Baixa' },
        { id: 'media', label: 'Média' },
        { id: 'alta', label: 'Alta' },
    ];

    // Status options
    const statusOptions = [
        { id: 'aberto', label: 'Aberto' },
        { id: 'em_progresso', label: 'Em Progresso' },
        { id: 'finalizado', label: 'Finalizado' },
        { id: 'cancelado', label: 'Cancelado' },
    ];

    // Calculate default SLA based on priority if it's a new ticket
    const calculateSLA = (prioridade: TicketPrioridade) => {
        const slaHours = {
            baixa: 72,
            media: 24,
            alta: 4,
        };

        return addHours(new Date(), slaHours[prioridade] || 24);
    };

    // Handle priority change and update SLA
    const handlePriorityChange = (newPriority: string | null) => {
        if (!newPriority) return;

        const priority = newPriority as TicketPrioridade;
        if (isNew) {
            onChange({
                ...value,
                prioridade: priority,
                sla_deadline: calculateSLA(priority)
            });
        } else {
            onChange({ ...value, prioridade: priority });
        }
    };

    const [objCliente, setObjCliente] = useState<any>(null);
    const [objClienteFieldErrors, setObjClienteFieldErrors] = useState<Record<string, any>>({});

    const handleOpen = () => {
        setObjCliente({});
    }

    const handleClose = () => {
        setObjCliente(null);
    }

    const changeMutator = useMutation(
        async () => {
            if (!objCliente) return;

            setObjClienteFieldErrors({});
            if (objCliente.id) {
                return await axios.put(`/api/cliente/${objCliente.id}`, objCliente, {
                    withCredentials: true,
                });
            } else {
                return await axios.post(`/api/cliente`, objCliente, {
                    withCredentials: true,
                });
            }
        },
        {
            onSuccess: () => {
                setObjClienteFieldErrors({});
                queryClient.invalidateQueries('cliente/box');
                queryClient.invalidateQueries('cliente');
                setObjCliente(null);

                toast(!objCliente.id ? 'Contato adicionado com sucesso' : 'Contato alterado com sucesso', {
                    toastId: !objCliente.id ? 'add-success' : 'edit-success',
                    position: 'top-center',
                    type: 'success',
                    autoClose: 5000,
                });
            },
            onError: (err: any) => {
                if (err.response?.status === 422) {
                    setObjClienteFieldErrors(err.response?.data?.fieldErrors ?? {});
                }
                toast(err.response?.data?.msg ?? 'Erro desconhecido', {
                    toastId: !objCliente.id ? 'add-error' : 'edit-error',
                    position: 'top-center',
                    type: 'warning',
                    autoClose: 5000,
                });
            },
        },
    );

    const handleConfirm = () => {
        changeMutator.mutate();
    }

    return (
        <>
            <Modal
                title="Adicionar cliente"
                show={objCliente != null}
                onClose={handleClose}
                onConfirm={handleConfirm}
            >
                <FormCliente value={objCliente} onChange={setObjCliente} fieldErrors={objClienteFieldErrors} />
            </Modal>
            <Grid>
                <GridItem sm={12}>
                    <CampoTexto
                        instanceId='titulo'
                        value={value?.titulo ?? ''}
                        onChange={v => onChange({ ...value, titulo: v })}
                        label="Título"
                        error={fieldErrors?.titulo}
                        required
                    />
                </GridItem>

                <GridItem sm={12}>
                    <CampoMemo
                        instanceId='descricao'
                        value={value?.descricao ?? ''}
                        onChange={v => onChange({ ...value, descricao: v })}
                        label="Descrição"
                        error={fieldErrors?.descricao}
                        placeholder="Descreva o ticket em detalhes..."
                        rows={5}
                        min={20}
                        max={2500}
                    />
                </GridItem>

                <GridItem sm={12} md={6}>
                    <HBox style={{ alignItems: 'flex-end' }}>
                        <CampoSeletorCliente
                            instanceId='cliente_id'
                            value={value?.cliente_id}
                            onChange={(v: string | null) => onChange({ ...value, cliente_id: v })}
                            label="Cliente"
                            error={fieldErrors?.cliente_id}
                            required
                            showSearch
                            searchFields={['label']}
                            onNovoCliente={handleOpen}
                        />
                    </HBox>
                </GridItem>

                <GridItem sm={12} md={6}>
                    <CampoSeletor
                        instanceId='usuario_responsavel_id'
                        value={value?.usuario_responsavel_id?.toString() || null}
                        onChange={v => onChange({ ...value, usuario_responsavel_id: v ? parseInt(v, 10) : null })}
                        label="Usuário Responsável"
                        error={fieldErrors?.usuario_responsavel_id}
                        options={
                            usuariosQuery.data?.map((u: any) => ({
                                id: u.id,
                                label: u.nome
                            })) || []
                        }
                        loading={usuariosQuery.isLoading}
                        showSearch
                        searchFields={['label']}
                    />
                </GridItem>

                <GridItem sm={12} md={6}>
                    <CampoSeletor
                        instanceId='grupo_responsavel_id'
                        value={value?.grupo_responsavel_id?.toString() || null}
                        onChange={v => onChange({ ...value, grupo_responsavel_id: v ? parseInt(v, 10) : null })}
                        label="Departamento Responsável"
                        error={fieldErrors?.grupo_responsavel_id}
                        options={
                            gruposQuery.data?.map((g: any) => ({
                                id: g.id,
                                label: g.nome
                            })) || []
                        }
                        loading={gruposQuery.isLoading}
                        showSearch
                        searchFields={['label']}
                    />
                </GridItem>

                <GridItem sm={12} md={6}>
                    <CampoSeletor
                        instanceId='prioridade'
                        noSearch
                        value={value?.prioridade || 'media'}
                        onChange={handlePriorityChange}
                        label="Prioridade"
                        options={prioridadeOptions}
                        error={fieldErrors?.prioridade}
                        required
                    />
                </GridItem>

                {isEditing && (
                    <GridItem sm={12} md={6}>
                        <CampoSeletor
                            instanceId='status'
                            noSearch
                            value={value?.status || 'aberto'}
                            onChange={v => onChange({ ...value, status: v })}
                            label="Status"
                            options={statusOptions}
                            error={fieldErrors?.status}
                            required
                        />
                    </GridItem>
                )}

                {isEditing && (
                    <GridItem sm={12} md={6}>
                        <CampoData
                            instanceId='sla_deadline'
                            value={value?.sla_deadline ? format(new Date(value.sla_deadline), 'yyyy-MM-dd HH:mm') : undefined}
                            onChange={v => onChange({ ...value, sla_deadline: v ? new Date(v) : undefined })}
                            label="Prazo SLA"
                            error={fieldErrors?.sla_deadline}
                            type="datetime-local"
                        />
                    </GridItem>
                )}

                {value?.status === "finalizado" && (
                    <GridItem sm={12}>
                        <CampoMemo
                            instanceId="descricao_resolucao"
                            value={value?.descricao_resolucao || ''}
                            onChange={(v: string) => onChange({ ...value, descricao_resolucao: v })}
                            label="Descrição da Resolução"
                            error={fieldErrors?.descricao_resolucao}
                            required
                            rows={3}
                            min={10}
                            max={1000}
                        />
                    </GridItem>
                )}

                {value?.status === "cancelado" && (
                    <GridItem sm={12}>
                        <CampoMemo
                            instanceId="motivo_cancelamento"
                            value={value?.motivo_cancelamento || ''}
                            onChange={(v: string) => onChange({ ...value, motivo_cancelamento: v })}
                            label="Motivo do Cancelamento"
                            error={fieldErrors?.motivo_cancelamento}
                            required
                            rows={3}
                            min={10}
                            max={1000}
                        />
                    </GridItem>
                )}

                {value?.status === "finalizado" && !value?.descricao_resolucao && (
                    <GridItem sm={12}>
                        <AlertBox variant="warning">
                            É necessário fornecer uma descrição da resolução ao finalizar um ticket.
                        </AlertBox>
                    </GridItem>
                )}

                {value?.status === "cancelado" && !value?.motivo_cancelamento && (
                    <GridItem sm={12}>
                        <AlertBox variant="danger">
                            É necessário fornecer um motivo ao cancelar um ticket.
                        </AlertBox>
                    </GridItem>
                )}

                {isNew && (
                    <GridItem sm={12}>
                        <AlertBox variant="info">
                            Após criar o ticket, você poderá adicionar atividades e anexos na página de detalhes.
                        </AlertBox>
                    </GridItem>
                )}
            </Grid>
        </>
    );
};
